/*
 * DBK Retailer Locator
 * -------------------------------------------------------------
 * Premium store locator: MapLibre GL vector map (no API key needed),
 * live search, region filter, per-retailer detail (hours / address /
 * photo / directions), "open now" badge, marker clustering, and a
 * request-a-retailer form.
 *
 * The pure logic (search, geo, hours parsing, geojson) is exported for
 * unit testing under Node; all DOM/map wiring is guarded to the browser.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api; // Node / tests
  if (typeof window !== 'undefined') {
    window.DBKRetailerLocator = api;
    if (typeof document !== 'undefined') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', api.initAll);
      } else {
        api.initAll();
      }
    }
  }
})(this, function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Pure helpers (unit-tested)
  // ---------------------------------------------------------------------------

  /** Lowercase + fold Danish/diacritic accents for accent-insensitive search. */
  function normalizeText(s) {
    s = (s == null ? '' : String(s)).toLowerCase();
    s = s.replace(/æ/g, 'ae').replace(/ø/g, 'o').replace(/å/g, 'a');
    if (s.normalize) s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    s = s.replace(/aa/g, 'a'); // "Aarhus"=="Århus", "gaard"=="gård"
    return s;
  }

  /** Filter retailers by free-text query and region. Returns a new array. */
  function filterRetailers(retailers, query, region) {
    var q = normalizeText(query).trim();
    var terms = q ? q.split(/\s+/) : [];
    return (retailers || []).filter(function (r) {
      if (region && region !== 'all' && (r.region || '') !== region) return false;
      if (!terms.length) return true;
      var hay = normalizeText(
        [r.name, r.city, r.postal, r.address, r.type, r.region, r.description]
          .filter(Boolean).join(' ')
      );
      return terms.every(function (t) { return hay.indexOf(t) !== -1; });
    });
  }

  /** Distinct, sorted region list present in the data. */
  function regionsOf(retailers) {
    var seen = {};
    (retailers || []).forEach(function (r) { if (r.region) seen[r.region] = true; });
    return Object.keys(seen).sort(function (a, b) { return a.localeCompare(b, 'da'); });
  }

  /** Haversine distance in km between two [lat,lng] points. */
  function haversine(aLat, aLng, bLat, bLng) {
    var R = 6371, toRad = function (d) { return d * Math.PI / 180; };
    var dLat = toRad(bLat - aLat), dLng = toRad(bLng - aLng);
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
  }

  /** True when a retailer has usable numeric coordinates. */
  function hasCoords(r) {
    return r && isFinite(parseFloat(r.lat)) && isFinite(parseFloat(r.lng)) &&
      !(parseFloat(r.lat) === 0 && parseFloat(r.lng) === 0);
  }

  /** Build a GeoJSON FeatureCollection for MapLibre from retailers. */
  function buildGeoJSON(retailers) {
    return {
      type: 'FeatureCollection',
      features: (retailers || []).filter(hasCoords).map(function (r) {
        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [parseFloat(r.lng), parseFloat(r.lat)] },
          properties: { id: r.id, name: r.name, city: r.city || '' }
        };
      })
    };
  }

  /**
   * Resolve a search query to a Danish place (4-digit postal code or town name)
   * against the vendored dk-places.json dataset ({zips:[[nr,navn,lat,lng]],
   * towns:[[navn,lat,lng]] sorted largest-town-first}).
   * Returns {lat, lng, label} or null when the query isn't a place.
   */
  function resolvePlace(query, places) {
    if (!query || !places) return null;
    var zips = places.zips || [], towns = places.towns || [];
    var q = normalizeText(query).trim();
    if (!q) return null;
    var digits = q.replace(/\s+/g, '');
    if (/^\d{4}$/.test(digits)) {
      for (var i = 0; i < zips.length; i++) {
        if (zips[i][0] === digits) {
          return { lat: zips[i][2], lng: zips[i][3], label: zips[i][0] + ' ' + zips[i][1] };
        }
      }
      return null;
    }
    if (/\d/.test(q) || q.length < 3) return null; // mixed/short queries -> text filter
    if (!places.__nt) {
      places.__nt = towns.map(function (t) { return normalizeText(t[0]); });
      places.__nz = zips.map(function (z) { return normalizeText(z[1]); });
    }
    var pick = function (list, norm, toObj, exact) {
      var out = [];
      for (var i = 0; i < list.length; i++) {
        if (exact ? norm[i] === q : norm[i].lastIndexOf(q, 0) === 0) out.push(toObj(list[i]));
      }
      return out;
    };
    var town = function (t) { return { name: t[0], lat: t[1], lng: t[2] }; };
    var zip = function (z) { return { name: z[1], lat: z[2], lng: z[3] }; };
    var set = pick(towns, places.__nt, town, true);
    if (!set.length) set = pick(zips, places.__nz, zip, true);
    if (!set.length) set = pick(towns, places.__nt, town, false).concat(pick(zips, places.__nz, zip, false));
    if (!set.length) return null;
    // Centroid of the matches — unless they are spread far apart (duplicate
    // village names), then fall back to the first (= largest) match.
    var lat = 0, lng = 0;
    set.forEach(function (p) { lat += p.lat; lng += p.lng; });
    lat /= set.length; lng /= set.length;
    for (var j = 0; j < set.length; j++) {
      if (haversine(lat, lng, set[j].lat, set[j].lng) > 40) {
        return { lat: set[0].lat, lng: set[0].lng, label: set[0].name };
      }
    }
    return { lat: lat, lng: lng, label: set[0].name };
  }

  /** Danish-locale name comparator. */
  function byName(a, b) {
    return String(a.name || '').localeCompare(String(b.name || ''), 'da', { sensitivity: 'base' });
  }

  /** Pair each retailer with its distance from a point; nearest first, coordless last. */
  function rankByDistance(retailers, lat, lng) {
    return (retailers || []).map(function (r) {
      return { retailer: r, km: hasCoords(r) ? haversine(lat, lng, parseFloat(r.lat), parseFloat(r.lng)) : null };
    }).sort(function (a, b) {
      if (a.km == null && b.km == null) return byName(a.retailer, b.retailer);
      if (a.km == null) return 1;
      if (b.km == null) return -1;
      return (a.km - b.km) || byName(a.retailer, b.retailer);
    });
  }

  /** "3,4 km" under 10 km (Danish decimal comma), "23 km" above. */
  function formatKm(km) {
    if (km == null || !isFinite(km)) return '';
    var v = km < 10 ? Math.max(0.1, Math.round(km * 10) / 10) : Math.round(km);
    return String(v).replace('.', ',') + ' km';
  }

  /**
   * Road part of a street address ("Tarphagevej 2" -> "Tarphagevej"), used to
   * retry geocoding at road level when the exact house number isn't in DAWA
   * (institutions sometimes use postal/vanity numbers that don't exist in DAR).
   * Returns '' when there is no trailing number to strip.
   */
  function roadOf(address) {
    var a = String(address == null ? '' : address).trim();
    var road = a.replace(/\s+\d+[a-zA-ZÆØÅæøå]?(?:[-/]\d*[a-zA-Z]?)?\s*$/, '').trim();
    return (road && road !== a) ? road : '';
  }

  /**
   * Landsdel from a Danish postal code, so the theme editor's region select
   * can stay on "—" and still get the right filter chip.
   */
  function regionFromPostal(postal) {
    var p = parseInt(String(postal == null ? '' : postal).replace(/\D/g, ''), 10);
    if (!isFinite(p) || p < 1000 || p > 9999) return '';
    if (p >= 3700 && p <= 3799) return 'Bornholm';
    if (p >= 3800 && p <= 3999) return ''; // Færøerne/Grønland ranges — not a landsdel here
    if (p < 4000) return 'Hovedstaden';
    if (p < 5000) return 'Sjælland';
    if (p < 6000) return 'Fyn';
    return 'Jylland';
  }

  var DAY_MAP = {
    'man': 1, 'mandag': 1, 'mon': 1, 'monday': 1,
    'tir': 2, 'tirsdag': 2, 'tue': 2, 'tuesday': 2, 'tis': 2,
    'ons': 3, 'onsdag': 3, 'wed': 3, 'wednesday': 3,
    'tor': 4, 'torsdag': 4, 'thu': 4, 'thursday': 4, 'tors': 4,
    'fre': 5, 'fredag': 5, 'fri': 5, 'friday': 5,
    'lor': 6, 'lordag': 6, 'sat': 6, 'saturday': 6, 'lør': 6, 'lørdag': 6,
    'son': 0, 'sondag': 0, 'sun': 0, 'sunday': 0, 'søn': 0, 'søndag': 0
  };

  function dayIndex(token) {
    var t = normalizeText(token).replace(/\.$/, '');
    return DAY_MAP.hasOwnProperty(t) ? DAY_MAP[t] : null;
  }

  /**
   * Parse free-text opening hours into per-weekday intervals.
   * Accepts lines like "Man-Fre: 10-17", "Lør 10:00 - 14:00", "Søn: Lukket".
   * Returns { 0..6: [{open:minutes, close:minutes}] } (0 = Sunday).
   */
  function parseHours(text) {
    var out = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    if (!text) return out;
    String(text).split(/[\n;]+/).forEach(function (raw) {
      var line = raw.trim();
      if (!line) return;
      // day part is before the first colon UNLESS the colon is inside a time (e.g. "10:00")
      var m = line.match(/^([^0-9]+?)[\s:]+(.+)$/);
      if (!m) return;
      var dayPart = m[1], timePart = m[2];
      if (/lukket|closed/i.test(timePart)) return;
      // resolve days ("alle dage", range "man-fre", single, or comma list)
      var days = [];
      if (/^(alle( ?dage)?|hver ?dag|dagligt?)$/.test(normalizeText(dayPart).trim())) {
        days = [0, 1, 2, 3, 4, 5, 6];
      } else dayPart.split(/[,/]/).forEach(function (seg) {
        var rng = seg.split(/[-–]/);
        if (rng.length === 2) {
          var a = dayIndex(rng[0]), b = dayIndex(rng[1]);
          if (a != null && b != null) {
            var i = a;
            for (var guard = 0; guard < 8; guard++) { days.push(i); if (i === b) break; i = (i + 1) % 7; }
          }
        } else {
          var d = dayIndex(seg); if (d != null) days.push(d);
        }
      });
      var tm = timePart.match(/(\d{1,2})(?:[.:](\d{2}))?\s*[-–]\s*(\d{1,2})(?:[.:](\d{2}))?/);
      if (!tm) return;
      var open = parseInt(tm[1], 10) * 60 + (tm[2] ? parseInt(tm[2], 10) : 0);
      var close = parseInt(tm[3], 10) * 60 + (tm[4] ? parseInt(tm[4], 10) : 0);
      days.forEach(function (d) { out[d].push({ open: open, close: close }); });
    });
    return out;
  }

  /** "open" | "closed" | null (unknown) for a given Date against hours text. */
  function openState(text, now) {
    var parsed = parseHours(text);
    var any = Object.keys(parsed).some(function (k) { return parsed[k].length; });
    if (!any) return null;
    var d = now.getDay(), mins = now.getHours() * 60 + now.getMinutes();
    return parsed[d].some(function (iv) { return mins >= iv.open && mins < iv.close; })
      ? 'open' : 'closed';
  }

  /** Directions URL — Apple Maps on iOS / Safari-on-Mac, else Google Maps. */
  function directionsUrl(r, ua) {
    ua = ua || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    var q = hasCoords(r)
      ? (parseFloat(r.lat) + ',' + parseFloat(r.lng))
      : encodeURIComponent([r.name, r.address, r.city].filter(Boolean).join(', '));
    var isIOS = /iphone|ipad|ipod/i.test(ua);
    var isMacSafari = /macintosh/i.test(ua) && /safari/i.test(ua) &&
      !/chrome|chromium|crios|fxios|edg|opr/i.test(ua);
    if (isIOS || isMacSafari) {
      return 'https://maps.apple.com/?q=' + q;
    }
    return 'https://www.google.com/maps/search/?api=1&query=' + q;
  }

  function esc(s) {
    return (s == null ? '' : String(s)).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Inline SVG icons (currentColor) — unicode glyphs like ◷/☎ render as tofu
  // on some Android system fonts.
  var SVG_ATTRS = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"';
  var ICONS = {
    clock: '<svg ' + SVG_ATTRS + '><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
    pin: '<svg ' + SVG_ATTRS + '><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>',
    phone: '<svg ' + SVG_ATTRS + '><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.9.6 2.8.7a2 2 0 0 1 1.7 2Z"/></svg>'
  };

  // ---------------------------------------------------------------------------
  // Browser wiring
  // ---------------------------------------------------------------------------

  var RASTER_FALLBACK_STYLE = {
    version: 8,
    sources: {
      osm: {
        type: 'raster',
        tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png'],
        tileSize: 256,
        attribution: '© OpenStreetMap contributors'
      }
    },
    layers: [{ id: 'osm', type: 'raster', source: 'osm' }]
  };

  function geocodeMissing(retailers, cacheKey) {
    // Auto-pin retailers that have an address but no coordinates, via DAWA
    // (Denmark's official address register: building-precise, free, no key,
    // CORS-enabled). Cached in localStorage. Coordinates entered in the theme
    // editor always win; retailers DAWA can't match fall back to their
    // postal/town centre later (see start()).
    if (typeof fetch === 'undefined') return Promise.resolve();
    var cache = {};
    try { cache = JSON.parse(localStorage.getItem(cacheKey) || '{}'); } catch (e) {}
    var save = function () {
      try { localStorage.setItem(cacheKey, JSON.stringify(cache)); } catch (e) {}
    };
    var lookup = function (q) {
      return fetch('https://api.dataforsyningen.dk/adgangsadresser?struktur=mini&per_side=1&q=' +
        encodeURIComponent(q))
        .then(function (res) { return res.json(); })
        .then(function (data) {
          return (data && data[0] && isFinite(data[0].y) && isFinite(data[0].x))
            ? [data[0].y, data[0].x] : null;
        });
    };
    var pending = retailers.filter(function (r) { return !hasCoords(r) && r.address; });
    return Promise.all(pending.map(function (r) {
      var key = normalizeText([r.address, r.postal, r.city].filter(Boolean).join(', '));
      if (cache[key]) { r.lat = cache[key][0]; r.lng = cache[key][1]; return Promise.resolve(); }
      var place = [r.postal, r.city].filter(Boolean).join(' ');
      var road = roadOf(r.address);
      return lookup([r.address, place].filter(Boolean).join(', '))
        .then(function (pt) {
          // exact house number not in DAWA -> retry at road level (still far
          // better than the postal-centre fallback)
          if (!pt && road) return lookup([road, place].filter(Boolean).join(', '));
          return pt;
        })
        .then(function (pt) {
          if (pt) { r.lat = pt[0]; r.lng = pt[1]; cache[key] = pt; save(); }
        }).catch(function () {});
    }));
  }

  function initAll() {
    if (typeof document === 'undefined') return;
    var nodes = document.querySelectorAll('[data-dbk-rl]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.__dbkInit) return; el.__dbkInit = true;
      try { initOne(el); } catch (e) { if (window.console) console.error('[DBK RL]', e); }
    });
  }

  function initOne(root) {
    var dataEl = root.querySelector('[data-dbk-rl-data]');
    if (!dataEl) return;
    var cfg = JSON.parse(dataEl.textContent || '{}');
    var retailers = cfg.retailers || [];
    retailers.forEach(function (r, i) {
      if (r.id == null) r.id = 'r' + i;
      if (!r.region) r.region = regionFromPostal(r.postal); // "—" in the editor still filters right
    });

    var els = {
      list: root.querySelector('[data-rl-list]'),
      count: root.querySelector('[data-rl-count]'),
      search: root.querySelector('[data-rl-search]'),
      chips: root.querySelector('[data-rl-chips]'),
      empty: root.querySelector('[data-rl-empty]'),
      map: root.querySelector('[data-rl-map]'),
      mapWrap: root.querySelector('[data-rl-map-wrap]'),
      requestBtn: root.querySelector('[data-rl-request-open]'),
      modal: root.querySelector('[data-rl-modal]'),
      modalClose: root.querySelectorAll('[data-rl-modal-close]'),
      typeSel: root.querySelector('[data-rl-request-type]'),
      viewBtns: root.querySelectorAll('[data-rl-view]'),
      sheet: root.querySelector('[data-rl-sheet]'),
      sheetBody: root.querySelector('[data-rl-sheet-body]'),
      sheetClose: root.querySelector('[data-rl-sheet-close]')
    };

    var state = { query: '', region: 'all', selected: null, map: null, markers: {},
      places: null, placesReq: null, placeKey: '' };
    var L = cfg.labels || {};
    var MOBILE_MQ = (typeof window !== 'undefined' && window.matchMedia)
      ? window.matchMedia('(max-width: 989px)') : { matches: false };
    function isMobile() { return !!MOBILE_MQ.matches; }

    // Lazy-load the Danish places dataset (postal codes + towns) that powers
    // the proximity search. Fetched once, from the theme's own CDN assets.
    function loadPlaces() {
      if (state.placesReq || !cfg.places_url || typeof fetch === 'undefined') return;
      state.placesReq = fetch(cfg.places_url)
        .then(function (res) { return res.json(); })
        .then(function (d) {
          state.places = d || null;
          if (state.query) render();
        })
        .catch(function () { state.places = null; });
    }

    // Portal the modal + sheet to <body> so they overlay everything (escape the
    // theme's per-section stacking context). Carry over any custom accent vars.
    (function () {
      var accent = root.style.getPropertyValue('--rl-accent');
      var accentDark = root.style.getPropertyValue('--rl-accent-dark');
      [els.modal, els.sheet].forEach(function (el) {
        if (!el || typeof document === 'undefined') return;
        if (accent) el.style.setProperty('--rl-accent', accent);
        if (accentDark) el.style.setProperty('--rl-accent-dark', accentDark);
        if (el.parentNode !== document.body) document.body.appendChild(el);
      });
    })();

    // ---- region chips ----
    if (els.chips) {
      var regions = regionsOf(retailers);
      if (regions.length) {
        var mk = function (val, label) {
          var b = document.createElement('button');
          b.type = 'button'; b.className = 'dbk-rl-chip'; b.textContent = label;
          b.setAttribute('data-region', val);
          if (val === 'all') b.setAttribute('aria-pressed', 'true');
          b.addEventListener('click', function () {
            state.region = val;
            state.selected = null; closeSheet();
            Array.prototype.forEach.call(els.chips.children, function (c) {
              c.setAttribute('aria-pressed', c === b ? 'true' : 'false');
            });
            render(); setSelectedPin(null);
          });
          return b;
        };
        els.chips.appendChild(mk('all', L.all_regions || 'Alle'));
        regions.forEach(function (rg) { els.chips.appendChild(mk(rg, rg)); });
      }
    }

    // ---- search ----
    if (els.search) {
      var t;
      els.search.addEventListener('focus', loadPlaces, { once: true });
      els.search.addEventListener('input', function () {
        loadPlaces();
        clearTimeout(t);
        t = setTimeout(function () {
          state.query = els.search.value;
          if (state.selected) { state.selected = null; closeSheet(); setSelectedPin(null); }
          render();
        }, 120);
      });
      // Enter on a phone keyboard: dismiss the keyboard, results are live already
      els.search.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); els.search.blur(); }
      });
    }

    // ---- list rendering ----
    function cardHtml(r, km) {
      var st = openState(r.hours, new Date());
      var badge = st === 'open'
        ? '<span class="dbk-rl-badge is-open">' + esc(L.open_now || 'Åben nu') + '</span>'
        : st === 'closed'
          ? '<span class="dbk-rl-badge is-closed">' + esc(L.closed_now || 'Lukket nu') + '</span>' : '';
      var dist = (km != null)
        ? '<span class="dbk-rl-dist">' + esc(formatKm(km)) + '</span>' : '';
      var thumb = r.image
        ? '<img class="dbk-rl-thumb" loading="lazy" src="' + esc(r.image) + '" alt="' + esc(r.name) + '">'
        : '<div class="dbk-rl-thumb dbk-rl-thumb--ph" aria-hidden="true">' +
          esc((r.name || '?').charAt(0)) + '</div>';
      return '' +
        '<article class="dbk-rl-card" tabindex="0" role="button" data-id="' + esc(r.id) + '" ' +
        'aria-label="' + esc(r.name) + '">' +
        thumb +
        '<div class="dbk-rl-card-body">' +
        '<div class="dbk-rl-card-top"><h3 class="dbk-rl-name">' + esc(r.name) + '</h3>' + badge + dist + '</div>' +
        (r.type ? '<p class="dbk-rl-type">' + esc(r.type) + '</p>' : '') +
        '<p class="dbk-rl-addr">' + esc([r.address, [r.postal, r.city].filter(Boolean).join(' ')]
          .filter(Boolean).join(', ')) + '</p>' +
        '</div><span class="dbk-rl-card-chevron" aria-hidden="true">›</span>' +
        '</article>';
    }

    function detailHtml(r) {
      var st = openState(r.hours, new Date());
      var rows = [];
      if (r.hours) {
        var badge = st === 'open' ? ' <em class="dbk-rl-inline-badge is-open">' + esc(L.open_now || 'Åben nu') + '</em>'
          : st === 'closed' ? ' <em class="dbk-rl-inline-badge is-closed">' + esc(L.closed_now || 'Lukket nu') + '</em>' : '';
        rows.push('<div class="dbk-rl-d-row"><span class="dbk-rl-d-ico">' + ICONS.clock + '</span><div><strong>' +
          esc(L.hours || 'Åbningstider') + badge + '</strong><div class="dbk-rl-hours">' +
          esc(r.hours).replace(/\n/g, '<br>') + '</div></div></div>');
      }
      var addr = [r.address, [r.postal, r.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
      if (addr) rows.push('<div class="dbk-rl-d-row"><span class="dbk-rl-d-ico">' + ICONS.pin + '</span><div><strong>' +
        esc(L.address || 'Adresse') + '</strong><div>' + esc(addr) + '</div></div></div>');
      if (r.phone) rows.push('<div class="dbk-rl-d-row"><span class="dbk-rl-d-ico">' + ICONS.phone + '</span><div><strong>' +
        esc(L.phone || 'Telefon') + '</strong><div><a href="tel:' + esc(r.phone.replace(/\s/g, '')) + '">' +
        esc(r.phone) + '</a></div></div></div>');
      var actions = '<div class="dbk-rl-d-actions">' +
        '<a class="dbk-rl-btn dbk-rl-btn--primary" target="_blank" rel="noopener" href="' +
        esc(directionsUrl(r)) + '">' + esc(L.directions || 'Rutevejledning') + '</a>' +
        (r.website ? '<a class="dbk-rl-btn" target="_blank" rel="noopener" href="' + esc(r.website) + '">' +
          esc(L.website || 'Hjemmeside') + '</a>' : '') + '</div>';
      var img = r.image ? '<img class="dbk-rl-d-img" loading="lazy" src="' + esc(r.image) + '" alt="' + esc(r.name) + '">' : '';
      return '<div class="dbk-rl-detail">' + img +
        '<h3 class="dbk-rl-d-name">' + esc(r.name) + '</h3>' +
        (r.type ? '<p class="dbk-rl-d-type">' + esc(r.type) + '</p>' : '') +
        (r.description ? '<p class="dbk-rl-d-desc">' + esc(r.description) + '</p>' : '') +
        rows.join('') + actions + '</div>';
    }

    function render() {
      // A query that resolves to a Danish place (city / postal code) keeps ALL
      // retailers visible, sorted nearest-first with the distance on each card.
      // Anything else filters by text as before.
      var place = state.query ? resolvePlace(state.query, state.places) : null;
      var filtered, kmById = null;
      if (place) {
        kmById = {};
        var ranked = rankByDistance(filterRetailers(retailers, '', state.region), place.lat, place.lng);
        ranked.forEach(function (e) { if (e.km != null) kmById[e.retailer.id] = e.km; });
        filtered = ranked.map(function (e) { return e.retailer; });
      } else {
        filtered = filterRetailers(retailers, state.query, state.region);
        filtered.sort(byName);
      }
      if (els.count) {
        var n = filtered.length + ' ' +
          (filtered.length === 1 ? (L.result_one || 'forhandler') : (L.result_many || 'forhandlere'));
        els.count.textContent = place ? n + ' · ' + (L.nearest || 'nærmest') + ' ' + place.label : n;
      }
      if (els.list) {
        var mobile = isMobile();
        els.list.innerHTML = filtered.map(function (r) {
          var km = kmById ? kmById[r.id] : null;
          if (r.id !== state.selected) return cardHtml(r, km);
          var active = cardHtml(r, km).replace('dbk-rl-card"', 'dbk-rl-card is-active"');
          // on mobile the detail lives in the bottom sheet, not inline
          return mobile ? active : active + detailWrap(r);
        }).join('');
        bindCards();
      }
      if (els.empty) els.empty.hidden = filtered.length !== 0;
      updateMap(filtered);
      syncPlaceOnMap(place);
    }

    // Show a "searched from here" dot and fly to a newly resolved place;
    // fly back to the full overview when the place is cleared.
    function syncPlaceOnMap(place) {
      if (!state.map || !state.mapReady) return;
      var key = place ? place.lat.toFixed(4) + ',' + place.lng.toFixed(4) : '';
      var src = state.map.getSource('search-origin');
      if (src) {
        src.setData({
          type: 'FeatureCollection',
          features: place ? [{ type: 'Feature', properties: {},
            geometry: { type: 'Point', coordinates: [place.lng, place.lat] } }] : []
        });
      }
      if (key === state.placeKey) return;
      state.placeKey = key;
      if (place) state.map.flyTo({ center: [place.lng, place.lat], zoom: 8.5, speed: 1.4 });
      else fitAll(state.map);
    }

    function detailWrap(r) {
      return '<div class="dbk-rl-detail-wrap" data-detail="' + esc(r.id) + '">' + detailHtml(r) + '</div>';
    }

    function bindCards() {
      Array.prototype.forEach.call(els.list.querySelectorAll('.dbk-rl-card'), function (card) {
        var id = card.getAttribute('data-id');
        var act = function () { select(id, true); };
        card.addEventListener('click', act);
        card.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); act(); }
        });
      });
    }

    function select(id, fly) {
      state.selected = (state.selected === id) ? null : id;
      render();
      setSelectedPin(state.selected);
      var r = retailers.filter(function (x) { return x.id === id; })[0];
      var mobile = isMobile();
      if (state.selected && r) {
        var card = els.list.querySelector('.dbk-rl-card[data-id="' + cssEsc(id) + '"]');
        if (card && card.scrollIntoView) card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        if (mobile) {
          openSheet(r);
          if (fly && state.map && hasCoords(r) && root.classList.contains('dbk-rl--mobile-map')) {
            // offset upward so the pin isn't hidden behind the bottom sheet
            state.map.flyTo({ center: [parseFloat(r.lng), parseFloat(r.lat)], zoom: 12, speed: 0.8, offset: [0, -110] });
          }
        } else if (fly && state.map && hasCoords(r)) {
          state.map.flyTo({ center: [parseFloat(r.lng), parseFloat(r.lat)], zoom: 12, speed: 0.8 });
          openPopup(r);
        }
      } else {
        if (mobile) closeSheet();
        else if (popup) popup.remove();
      }
    }

    function cssEsc(s) { return String(s).replace(/["\\]/g, '\\$&'); }

    // ---- mobile view toggle + detail sheet ----
    function setView(view) {
      root.classList.toggle('dbk-rl--mobile-map', view === 'map');
      root.classList.toggle('dbk-rl--mobile-list', view === 'list');
      if (els.viewBtns) {
        Array.prototype.forEach.call(els.viewBtns, function (b) {
          b.setAttribute('aria-pressed', b.getAttribute('data-rl-view') === view ? 'true' : 'false');
        });
      }
      if (view === 'map') {
        if (!state.map) { start(); }
        else { setTimeout(function () {
          state.map.resize();
          if (!state.selected) fitAll(state.map);
        }, 30); }
      }
    }

    function openSheet(r) {
      if (!els.sheet || !els.sheetBody) return;
      var extra = root.classList.contains('dbk-rl--mobile-list')
        ? '<div class="dbk-rl-d-actions"><button type="button" class="dbk-rl-btn dbk-rl-btn--primary" ' +
          'data-rl-sheet-tomap>' + esc(L.show_on_map || 'Vis på kort') + '</button></div>'
        : '';
      els.sheetBody.innerHTML = detailHtml(r) + extra;
      els.sheet.classList.add('is-open');
      els.sheet.setAttribute('aria-hidden', 'false');
      var tm = els.sheetBody.querySelector('[data-rl-sheet-tomap]');
      if (tm) tm.addEventListener('click', function () {
        setView('map');
        if (state.map && hasCoords(r)) setTimeout(function () {
          state.map.flyTo({ center: [parseFloat(r.lng), parseFloat(r.lat)], zoom: 12, speed: 0.8, offset: [0, -110] });
        }, 60);
      });
    }

    function closeSheet() {
      if (!els.sheet) return;
      els.sheet.classList.remove('is-open');
      els.sheet.setAttribute('aria-hidden', 'true');
    }

    // swipe-down on the sheet (when scrolled to top) dismisses it
    if (els.sheet) {
      var dragY = null, dragDelta = 0;
      els.sheet.addEventListener('touchstart', function (e) {
        if (els.sheet.scrollTop > 0) { dragY = null; return; }
        dragY = e.touches[0].clientY; dragDelta = 0;
        els.sheet.classList.add('is-dragging');
      }, { passive: true });
      els.sheet.addEventListener('touchmove', function (e) {
        if (dragY == null) return;
        dragDelta = e.touches[0].clientY - dragY;
        if (dragDelta > 0) els.sheet.style.transform = 'translateY(' + dragDelta + 'px)';
      }, { passive: true });
      els.sheet.addEventListener('touchend', function () {
        if (dragY == null) return;
        dragY = null;
        els.sheet.classList.remove('is-dragging');
        els.sheet.style.transform = '';
        if (dragDelta > 90) { state.selected = null; render(); setSelectedPin(null); closeSheet(); }
      });
    }

    // ---- map ----
    function updateMap(filtered) {
      if (!state.map || !state.mapReady) return;
      var src = state.map.getSource('retailers');
      if (src) src.setData(buildGeoJSON(filtered));
    }

    var popup = null;
    function openPopup(r) {
      if (!state.map || !window.maplibregl || !hasCoords(r)) return;
      if (popup) popup.remove();
      popup = new window.maplibregl.Popup({ offset: 18, closeButton: true, className: 'dbk-rl-popup' })
        .setLngLat([parseFloat(r.lng), parseFloat(r.lat)])
        .setHTML('<strong>' + esc(r.name) + '</strong>' + (r.city ? '<br>' + esc(r.city) : '') +
          '<br><button type="button" class="dbk-rl-popup-link" data-goto="' + esc(r.id) + '">' +
          esc(L.see_details || 'Se detaljer') + '</button>')
        .addTo(state.map);
      var node = popup.getElement();
      if (node) {
        var btn = node.querySelector('[data-goto]');
        // if already selected, don't toggle it OFF — just bring the card into view
        if (btn) btn.addEventListener('click', function () {
          if (state.selected !== r.id) { select(r.id, false); return; }
          var card = els.list.querySelector('.dbk-rl-card[data-id="' + cssEsc(r.id) + '"]');
          if (card && card.scrollIntoView) card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    }

    function initMap() {
      if (!window.maplibregl || !els.map) { if (els.mapWrap) els.mapWrap.classList.add('dbk-rl-nomap'); return; }
      var style = cfg.map_style_url || 'https://tiles.openfreemap.org/styles/liberty';
      if (cfg.mapbox_token && /mapbox/i.test(style)) {
        style = style + (style.indexOf('?') === -1 ? '?' : '&') + 'access_token=' + cfg.mapbox_token;
      }
      var map;
      try {
        map = new window.maplibregl.Map({
          container: els.map, style: style,
          center: [cfg.center_lng || 11.4, cfg.center_lat || 56.1],
          zoom: cfg.zoom || 6, attributionControl: true, cooperativeGestures: true
        });
      } catch (e) { if (els.mapWrap) els.mapWrap.classList.add('dbk-rl-nomap'); return; }
      state.map = map;
      root.__dbkMap = map; // debug/test hook
      map.addControl(new window.maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      map.on('error', function (e) {
        // vector style failed -> raster fallback, once (then re-add our layers)
        if (!state.rasterTried && e && e.error && /style|tiles|fetch/i.test(String(e.error.message || ''))) {
          state.rasterTried = true;
          try {
            map.setStyle(RASTER_FALLBACK_STYLE);
            map.once('style.load', function () { setupLayers(map); });
          } catch (_) {}
        }
      });
      map.on('load', function () { setupLayers(map); });
    }

    /** Build a Google-style teardrop pin (brand color) and register it once. */
    // Draw a Google-style teardrop pin (brand color) on a canvas and register it.
    // Canvas -> ImageData is synchronous and deterministic (no image-decode timing).
    function ensurePin(map, color, cb) {
      try {
        if (map.hasImage && map.hasImage('dbk-pin')) { cb(); return; }
        var dpr = 2, W = 27, H = 39, cw = W * dpr, ch = H * dpr;
        var cv = document.createElement('canvas'); cv.width = cw; cv.height = ch;
        var ctx = cv.getContext('2d'); ctx.scale(dpr, dpr);
        var r = (W / 2) - 2, cx = W / 2, cy = r + 2, tip = H - 1.5;
        ctx.beginPath();
        ctx.arc(cx, cy, r, Math.PI, 2 * Math.PI, false);        // top half (north)
        ctx.quadraticCurveTo(cx + r, cy + r * 1.25, cx, tip);   // right side -> tip
        ctx.quadraticCurveTo(cx - r, cy + r * 1.25, cx - r, cy); // tip -> left side
        ctx.closePath();
        ctx.fillStyle = color; ctx.fill();
        ctx.lineWidth = 1.5; ctx.strokeStyle = '#ffffff'; ctx.stroke();
        ctx.beginPath(); ctx.arc(cx, cy, r * 0.42, 0, 2 * Math.PI);
        ctx.fillStyle = '#ffffff'; ctx.fill();
        map.addImage('dbk-pin', ctx.getImageData(0, 0, cw, ch), { pixelRatio: dpr });
      } catch (e) { if (window.console) console.warn('[DBK RL] pin image failed', e); }
      cb();
    }

    function fitAll(map) {
      var pts = retailers.filter(hasCoords);
      if (!pts.length || !window.maplibregl) return;
      if (pts.length === 1) {
        map.jumpTo({ center: [parseFloat(pts[0].lng), parseFloat(pts[0].lat)], zoom: 11 });
        return;
      }
      var b = new window.maplibregl.LngLatBounds();
      pts.forEach(function (r) { b.extend([parseFloat(r.lng), parseFloat(r.lat)]); });
      map.fitBounds(b, { padding: { top: 60, bottom: 60, left: 60, right: 60 }, maxZoom: 12, duration: 0 });
    }

    // Emphasise the selected pin via a filtered overlay layer (feature-state is
    // NOT allowed in layout props like icon-size, so we swap a filter instead).
    function setSelectedPin(id) {
      var map = state.map;
      if (!map || !map.getLayer('unclustered-selected')) return;
      try {
        map.setFilter('unclustered-selected', ['all', ['!', ['has', 'point_count']],
          ['==', ['get', 'id'], id == null ? '__dbk_none__' : id]]);
      } catch (e) {}
    }

    function setupLayers(map) {
      var color = cfg.marker_color || '#C8892B';
      // "searched from here" origin dot (added first = drawn under the pins)
      if (!map.getSource('search-origin')) {
        map.addSource('search-origin', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      }
      if (!map.getLayer('search-origin-halo')) {
        map.addLayer({
          id: 'search-origin-halo', type: 'circle', source: 'search-origin',
          paint: { 'circle-color': '#2f6fdb', 'circle-opacity': 0.18, 'circle-radius': 16 }
        });
      }
      if (!map.getLayer('search-origin-dot')) {
        map.addLayer({
          id: 'search-origin-dot', type: 'circle', source: 'search-origin',
          paint: { 'circle-color': '#2f6fdb', 'circle-radius': 6.5,
            'circle-stroke-width': 2.5, 'circle-stroke-color': '#fff' }
        });
      }
      if (!map.getSource('retailers')) {
        map.addSource('retailers', {
          type: 'geojson', data: buildGeoJSON(retailers),
          cluster: cfg.cluster === true, clusterRadius: 45, clusterMaxZoom: 11
        });
      } else {
        map.getSource('retailers').setData(buildGeoJSON(retailers));
      }
      if (!map.getLayer('clusters')) {
        map.addLayer({
          id: 'clusters', type: 'circle', source: 'retailers', filter: ['has', 'point_count'],
          paint: {
            'circle-color': color, 'circle-opacity': 0.92,
            'circle-radius': ['step', ['get', 'point_count'], 18, 5, 24, 15, 30],
            'circle-stroke-width': 3, 'circle-stroke-color': '#fff'
          }
        });
      }
      if (!map.getLayer('cluster-count')) {
        map.addLayer({
          id: 'cluster-count', type: 'symbol', source: 'retailers', filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'], 'text-size': 13,
            'text-font': ['Noto Sans Bold', 'Open Sans Bold', 'Arial Unicode MS Bold']
          },
          paint: { 'text-color': '#fff' }
        });
      }
      ensurePin(map, color, function () {
        var baseLayout = {
          'icon-image': 'dbk-pin',
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,     // show every pin, never hide overlaps
          'icon-ignore-placement': true
        };
        if (!map.getLayer('unclustered')) {
          map.addLayer({
            id: 'unclustered', type: 'symbol', source: 'retailers',
            filter: ['!', ['has', 'point_count']],
            layout: Object.assign({}, baseLayout, { 'icon-size': 1 })
          });
        }
        if (!map.getLayer('unclustered-selected')) {
          map.addLayer({
            id: 'unclustered-selected', type: 'symbol', source: 'retailers',
            filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'id'], '__dbk_none__']],
            layout: Object.assign({}, baseLayout, { 'icon-size': 1.32 })
          });
        }
        attachHandlers(map);
        state.mapReady = true;
        fitAll(map);
        if (state.selected) setSelectedPin(state.selected);
        state.placeKey = ''; // re-sync any active place search onto the fresh style
        render();
      });
    }

    function attachHandlers(map) {
      if (state.handlersBound) return;
      state.handlersBound = true;
      map.on('click', 'clusters', function (e) {
        var f = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        if (!f.length) return;
        var cid = f[0].properties.cluster_id;
        map.getSource('retailers').getClusterExpansionZoom(cid, function (err, z) {
          if (err) return; map.easeTo({ center: f[0].geometry.coordinates, zoom: z });
        });
      });
      map.on('click', 'unclustered', function (e) {
        select(e.features[0].properties.id, true);
      });
      ['clusters', 'unclustered'].forEach(function (lyr) {
        map.on('mouseenter', lyr, function () { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', lyr, function () { map.getCanvas().style.cursor = ''; });
      });
    }

    // ---- request modal ----
    if (els.requestBtn && els.modal) {
      var lastFocus = null;
      var openModal = function () {
        lastFocus = document.activeElement; els.modal.hidden = false;
        document.body.style.overflow = 'hidden';
        var f = els.modal.querySelector('input,select,textarea,button');
        if (f) f.focus();
        document.addEventListener('keydown', onKey);
      };
      var closeModal = function () {
        els.modal.hidden = true; document.body.style.overflow = '';
        document.removeEventListener('keydown', onKey);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      };
      var onKey = function (e) {
        if (e.key === 'Escape') { closeModal(); return; }
        if (e.key !== 'Tab') return; // trap focus inside the dialog
        var f = els.modal.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };
      els.requestBtn.addEventListener('click', openModal);
      Array.prototype.forEach.call(els.modalClose, function (b) { b.addEventListener('click', closeModal); });
      els.modal.addEventListener('click', function (e) { if (e.target === els.modal) closeModal(); });
      // reopen automatically after a contact-form submit (success or errors)
      if (typeof window !== 'undefined' && window.dbkRlReopen) { openModal(); }
      // toggle shop-name field label based on request type
      if (els.typeSel) {
        var shopWrap = els.modal.querySelector('[data-rl-shopfield]');
        var sync = function () {
          if (shopWrap) shopWrap.style.display = '';
        };
        els.typeSel.addEventListener('change', sync); sync();
      }
    }

    // ---- mobile view toggle + sheet wiring ----
    if (els.viewBtns) {
      Array.prototype.forEach.call(els.viewBtns, function (b) {
        b.addEventListener('click', function () {
          closeSheet();
          setView(b.getAttribute('data-rl-view'));
        });
      });
    }
    if (els.sheetClose) {
      els.sheetClose.addEventListener('click', function () {
        state.selected = null; render(); setSelectedPin(null); closeSheet();
      });
    }
    var fullPlaceholder = els.search ? els.search.placeholder : '';
    function syncPlaceholder() {
      if (!els.search) return;
      els.search.placeholder = isMobile()
        ? (L.search_placeholder_short || 'Søg by eller postnummer')
        : fullPlaceholder;
    }
    syncPlaceholder();
    function onBreakpointChange() {
      syncPlaceholder();
      if (!isMobile()) closeSheet(); // desktop shows the detail inline instead
      render();
    }
    if (MOBILE_MQ.addEventListener) MOBILE_MQ.addEventListener('change', onBreakpointChange);
    else if (MOBILE_MQ.addListener) MOBILE_MQ.addListener(onBreakpointChange);
    // keep the map sized correctly across rotation / breakpoint changes
    var rz;
    window.addEventListener('resize', function () {
      clearTimeout(rz);
      rz = setTimeout(function () {
        if (state.map) state.map.resize();
        render(); // re-sync inline-vs-sheet detail when crossing the breakpoint
      }, 200);
    });

    // ---- boot ----
    render();
    setTimeout(loadPlaces, 1800); // prefetch so the first search is instant
    function start() {
      if (state.started) return;
      state.started = true;
      geocodeMissing(retailers, 'dbk_rl_geo_' + (cfg.section_id || 'x')).then(function () {
        // anything DAWA couldn't match still gets a pin at its postal/town centre
        var missing = retailers.filter(function (r) { return !hasCoords(r) && (r.postal || r.city); });
        if (!missing.length) return;
        loadPlaces();
        return (state.placesReq || Promise.resolve()).then(function () {
          missing.forEach(function (r) {
            var p = resolvePlace(r.postal || r.city, state.places);
            if (p) { r.lat = p.lat; r.lng = p.lng; }
          });
        });
      }).then(function () {
        render(); initMap();
      });
    }
    // lazy-init map when scrolled into view (perf)
    if ('IntersectionObserver' in window && els.mapWrap) {
      var io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) { io.disconnect(); start(); }
      }, { rootMargin: '250px' });
      io.observe(els.mapWrap);
      // if the map panel is the default mobile view it's already on-screen; ensure init
      setTimeout(function () { if (!state.started) start(); }, 1200);
    } else { start(); }
  }

  return {
    normalizeText: normalizeText,
    filterRetailers: filterRetailers,
    regionsOf: regionsOf,
    resolvePlace: resolvePlace,
    rankByDistance: rankByDistance,
    formatKm: formatKm,
    regionFromPostal: regionFromPostal,
    roadOf: roadOf,
    haversine: haversine,
    hasCoords: hasCoords,
    buildGeoJSON: buildGeoJSON,
    parseHours: parseHours,
    openState: openState,
    directionsUrl: directionsUrl,
    initAll: initAll,
    initOne: initOne
  };
});
